/**
 * HubComparison — para top-N items del hangar dockeado, "¿dónde vendo mejor?".
 *
 *  - Summary tile con la mejor oportunidad (item + hub + delta% vs Jita).
 *  - Distribución: Jita wins vs other hub wins.
 *  - Tabla con un item por fila, los 5 hubs en columnas.
 *  - Highlight el "best hub" de cada fila + delta vs Jita.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type HubKey = 'Jita' | 'Amarr' | 'Dodixie' | 'Rens' | 'Hek'

const HUB_KEYS: HubKey[] = ['Jita', 'Amarr', 'Dodixie', 'Rens', 'Hek']

export function HubComparison() {
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['utility-hub-comparison', forceRefetchKey],
    queryFn: () => api.hubComparison(force),
  })

  const items = data?.items ?? []
  const summary = data?.summary
  const hubs = data?.settings_used?.hubs ?? []

  const hubLabelByRegionId = useMemo(() => {
    const m = new Map<number, string>()
    for (const h of hubs) m.set(h.region_id, h.short)
    return m
  }, [hubs])

  const inSpace = data?.location?.kind === 'in_space'

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Hub comparison</div>
          <div className="text-eve-muted text-xs">
            Top-{data?.settings_used.top_n ?? 10} items del hangar dockeado —
            ¿dónde te conviene vender cada uno?
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary */}
      {summary && summary.items_analyzed > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <SummaryTile
            label="Mejor oportunidad"
            value={summary.best_opportunity?.name ?? '—'}
            sub={
              summary.best_opportunity
                ? `${summary.best_opportunity.best_hub} · ${formatPct(summary.best_opportunity.delta_vs_jita_pct)} vs Jita`
                : '—'
            }
            accent
          />
          <SummaryTile
            label="Jita gana"
            value={String(summary.jita_wins)}
            sub={`${summary.items_analyzed} items analizados`}
          />
          <SummaryTile
            label="Otro hub gana"
            value={String(summary.other_hub_wins)}
            sub={
              summary.other_hub_wins > 0
                ? 'Conviene mover stock a otro hub'
                : 'Jita es competitivo en todo'
            }
            positive={summary.other_hub_wins > 0}
          />
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-eve-muted text-xs uppercase tracking-wider border-b border-eve-border">
              <th className="py-2 pr-3 font-medium">Item</th>
              {HUB_KEYS.map((h) => (
                <th key={h} className="py-2 pr-3 font-medium text-right">
                  {h}
                </th>
              ))}
              <th className="py-2 pr-3 font-medium text-right">Δ vs Jita</th>
              <th className="py-2 font-medium text-center">Best</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={HUB_KEYS.length + 3}>
                  <LoadingSkeleton variant="rows" rows={4} />
                </td>
              </tr>
            ) : inSpace ? (
              <tr>
                <td colSpan={HUB_KEYS.length + 3} className="text-eve-muted py-8 text-center italic">
                  Estás en space — volve a dockear para que pueda leer tu hangar.
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={HUB_KEYS.length + 3} className="text-eve-muted py-8 text-center italic">
                  No tenés items en el hangar dockeado.
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <HubRow
                  key={it.type_id}
                  name={it.name}
                  qty={it.quantity}
                  hubPrices={it.hub_prices}
                  bestHub={it.best_hub}
                  delta={it.delta_vs_jita_pct}
                  bestTotal={it.best_total}
                  hubLabelByRegionId={hubLabelByRegionId}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-eve-muted mt-3">
        Best = highest buy order en cualquier hub (precio que recibís si vendés
        ahí). Delta vs Jita es cuánto más (% o menos) sacás vendiendo en el
        mejor hub comparado con Jita. Si delta es alto, vale la pena haul-ear.
      </p>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function SummaryTile({
  label,
  value,
  sub,
  accent,
  positive,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  positive?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : accent
      ? 'text-eve-accent'
      : 'text-eve-text'
  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3">
      <div className="stat-label">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 truncate ${valueClass}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function HubRow({
  name,
  qty,
  hubPrices,
  bestHub,
  delta,
  bestTotal,
  hubLabelByRegionId,
}: {
  name: string
  qty: number
  hubPrices: Record<string, { price: number | null; region_id: number }>
  bestHub: string | null
  delta: number | null
  bestTotal: number | null
  hubLabelByRegionId: Map<number, string>
}) {
  return (
    <tr className="border-b border-eve-border/40 hover:bg-eve-panel/30">
      <td className="py-2 pr-3">
        <div className="font-medium">{name}</div>
        <div className="text-[10px] text-eve-muted font-mono">
          × {qty.toLocaleString('en-US')}
        </div>
      </td>
      {HUB_KEYS.map((h) => {
        const cell = hubPrices[h]
        const price = cell?.price ?? null
        const isBest = bestHub === h
        const valueClass = isBest
          ? 'text-eve-success font-semibold'
          : price !== null
            ? 'text-eve-text'
            : 'text-eve-muted'
        return (
          <td key={h} className="py-2 pr-3 text-right">
            <div className={`font-mono ${valueClass}`}>
              {price !== null ? formatISK(price, 2) : '—'}
            </div>
            {isBest && (
              <div className="text-[10px] text-eve-success font-mono">
                ★ {bestTotal !== null ? formatISKShort(bestTotal) : ''}
              </div>
            )}
          </td>
        )
      })}
      <td className="py-2 pr-3 text-right font-mono">
        {delta !== null ? (
          <span
            className={
              delta >= 5
                ? 'text-eve-success'
                : delta <= -5
                  ? 'text-eve-danger'
                  : 'text-eve-muted'
            }
          >
            {formatPct(delta)}
          </span>
        ) : (
          <span className="text-eve-muted">—</span>
        )}
      </td>
      <td className="py-2 text-center">
        {bestHub !== null ? (
          <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-eve-success text-eve-success bg-eve-success/10">
            {bestHub}
          </span>
        ) : (
          <span className="text-eve-muted text-xs">—</span>
        )}
      </td>
    </tr>
  )
}

function formatPct(n: number | null): string {
  if (n === null) return '—'
  return (n >= 0 ? '+' : '') + n.toFixed(1) + '%'
}
